import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from './firebase';
import { getAuthToken, validateCoupon } from './api';
import useRazorpayPayment from './services/useRazorpayPayment';
import './PaymentPage.css';

const API_BASE = process.env.REACT_APP_API_BASE || "https://mockbot-api1.onrender.com";

/**
 * PaymentPage - Razorpay Payment Integration with Discount Codes
 * 
 * Displays 4 plan options in horizontal layout.
 * Supports three code types: Coupon, College, Company codes.
 * Each code validates against specific plans via backend.
 */
export default function PaymentPage({ onBack, onSuccess: onSuccessCallback }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTrialOffer = searchParams.get('trialOffer') === 'true';
  const { initiatePayment, loading, error, setError } = useRazorpayPayment();
  const [processingPlan, setProcessingPlan] = useState(null);
  
  // Code input states (three separate types)
  const [couponCode, setCouponCode] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  
  // Applied code states (one per type)
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [appliedCompany, setAppliedCompany] = useState(null);
  
  // Validation states
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [validatingCompany, setValidatingCompany] = useState(false);
  const [companyJobInfo, setCompanyJobInfo] = useState(null);

  // Plan definitions with new pricing
  const plans = [
    {
      id: 'SINGLE_INTERVIEW',
      name: '1 Interview',
      price: 199,
      priceDisplay: '₹199',
      description: 'Try it out',
      perUnit: '₹199 / interview',
      recommended: false
    },
    {
      id: 'FIVE_INTERVIEWS',
      name: '5 Interviews',
      price: 899,
      priceDisplay: '₹899',
      description: 'Most popular',
      perUnit: '₹180 / interview',
      recommended: true
    },
    {
      id: 'TEN_INTERVIEWS',
      name: '10 Interviews',
      price: 1599,
      priceDisplay: '₹1,599',
      description: 'Great value',
      perUnit: '₹160 / interview',
      recommended: false
    },
    {
      id: 'TWENTY_INTERVIEWS',
      name: '20 Interviews',
      price: 1899,
      priceDisplay: '₹1,899',
      description: 'Best value',
      perUnit: '₹95 / interview',
      recommended: false
    }
  ];

  /**
   * Handle Apply Coupon button click
   * Validates coupon against ALL plans and stores results
   */
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    setError(null);
    setValidatingCoupon(true);

    try {
      const code = couponCode.trim().toUpperCase();
      
      // Validate coupon against all plans in parallel
      const validationPromises = plans.map(plan => 
        validateCoupon(code, plan.id)
          .then(result => ({ planId: plan.id, ...result }))
          .catch(err => ({ planId: plan.id, valid: false, message: 'Validation failed' }))
      );
      
      const results = await Promise.all(validationPromises);
      
      // Check if coupon is valid for at least one plan
      const validForAnyPlan = results.some(r => r.valid);
      
      if (validForAnyPlan) {
        // Store validation results per plan
        const validationMap = {};
        results.forEach(r => {
          validationMap[r.planId] = {
            valid: r.valid,
            discount_percentage: r.discount_percentage,
            message: r.message
          };
        });
        
        setAppliedCoupon({
          code: code,
          validationMap: validationMap,
          valid: true
        });
        setError(null);
      } else {
        setAppliedCoupon(null);
        setError(results[0]?.message || 'Invalid coupon code');
      }
    } catch {
      setAppliedCoupon(null);
      setError('Failed to validate coupon. Please try again.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  /**
   * Handle Apply Company Code button click
   * Validates against /company/job/{code}/details endpoint
   */
  const handleApplyCompany = async () => {
    if (!companyCode.trim()) {
      setError('Please enter a company / job code');
      return;
    }

    setError(null);
    setValidatingCompany(true);

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Please log in to validate company code');
        setValidatingCompany(false);
        return;
      }

      const code = companyCode.trim();
      const response = await fetch(`${API_BASE}/company/job/${code}/details`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyJobInfo({
          company_name: data.company_name || data.org_id || '',
          job_name: data.job_name || '',
          years: data.years || '',
          difficulty: data.difficulty || ''
        });

        setAppliedCompany({
          code: code,
          valid: true,
          job_name: data.job_name || ''
        });
        setError(null);
      } else {
        setAppliedCompany(null);
        setCompanyJobInfo(null);
        setError('Invalid company code. Please check and try again.');
      }
    } catch {
      setAppliedCompany(null);
      setCompanyJobInfo(null);
      setError('Failed to validate company code. Please try again.');
    } finally {
      setValidatingCompany(false);
    }
  };

  /**
   * Remove applied codes
   */
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setError(null);
  };

  const handleRemoveCompany = () => {
    setAppliedCompany(null);
    setCompanyCode('');
    setCompanyJobInfo(null);
    setError(null);
  };

  /**
   * Get price display for a plan
   * Applies coupon discount percentage if valid for this specific plan
   */
  const getPriceForPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    const basePrice = plan.price;

    // Trial offer: 50% off all plans
    if (isTrialOffer) {
      return {
        original: basePrice,
        final: Math.round(basePrice * 0.5),
        hasDiscount: true,
        discountPercentage: 50,
        codeType: 'trial'
      };
    }

    // Check if coupon is valid for THIS specific plan
    if (appliedCoupon && appliedCoupon.valid && appliedCoupon.validationMap) {
      const planValidation = appliedCoupon.validationMap[planId];
      
      if (planValidation && planValidation.valid) {
        const discountAmount = basePrice * (planValidation.discount_percentage / 100);
        const finalPrice = Math.max(0, basePrice - discountAmount);
        
        return {
          original: basePrice,
          final: Math.round(finalPrice),
          hasDiscount: true,
          discountPercentage: planValidation.discount_percentage || 0,
          codeType: 'coupon'
        };
      }
    }
    
    // No discount - return base price
    return {
      original: null,
      final: basePrice,
      hasDiscount: false,
      discountPercentage: 0
    };
  };

  /**
   * Handle Buy button click
   * @param {string} planId - Plan type ID
   */
  const handleBuy = async (planId) => {
    // Clear previous error
    setError(null);
    setProcessingPlan(planId);

    // Get user info
    const user = auth.currentUser;
    if (!user) {
      setError('Please log in to continue');
      setProcessingPlan(null);
      return;
    }

    // Use applied coupon code if available and valid for this plan
    let discountCodeValue = null;
    if (appliedCoupon && appliedCoupon.valid && appliedCoupon.validationMap) {
      const planValidation = appliedCoupon.validationMap[planId];
      if (planValidation && planValidation.valid) {
        discountCodeValue = appliedCoupon.code;
      }
    }

    // Initiate payment with optional discount code / trial offer
    await initiatePayment({
      planType: planId,
      userEmail: user.email || '',
      userName: user.displayName || '',
      userPhone: user.phoneNumber || '',
      discountCode: discountCodeValue,
      trialOffer: isTrialOffer,

      // Success callback
      onSuccess: (response) => {
        setProcessingPlan(null);
        setCouponCode('');
        setAppliedCoupon(null);
        
        // Call parent success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(response);
        }
      },

      // Failure callback
      onFailure: (error) => {
        setProcessingPlan(null);
        
        // Set error message based on error code
        if (error.code === 'PAYMENT_CANCELLED') {
          setError('Payment cancelled. Please try again when ready.');
        } else if (error.code === 'VERIFICATION_FAILED') {
          setError('Payment verification failed. Please contact support.');
        } else if (error.code === 'SCRIPT_LOAD_FAILED') {
          setError('Payment gateway unavailable. Please refresh the page.');
        } else if (error.code === 'INVALID_COUPON') {
          setError(error.error || 'Invalid coupon code. Please check and try again.');
        } else if (error.code === 'MOCK_ORDER') {
          setError('⚠️ Test Mode: Payment gateway not configured. Use a discount code (e.g., DEV_FREE) or contact support to enable payments.');
        } else {
          setError(error.error || 'Payment failed. Please try again.');
        }
      },

      // Modal dismissed callback
      onDismiss: () => {
        setProcessingPlan(null);
        setError('Payment cancelled.');
      }
    });
  };

  return (
    <div className="payment-page-container">
      {/* Back/Logout button */}
      <div className="app-bg-blobs" aria-hidden="true">
        <div className="app-blob app-blob-1" />
        <div className="app-blob app-blob-2" />
        <div className="app-blob app-blob-3" />
      </div>
      {onBack && (
        <button 
          onClick={onBack} 
          className="payment-page-back-button"
          disabled={loading}
        >
          Logout
        </button>
      )}

      <div className="payment-page-card">
        {/* Header */}
        <div className="payment-page-header">
          <div className="payment-page-logo-container">
            <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="payment-page-logo" />
          </div>
          <h1 className="payment-page-title">Ace Your Next Interview</h1>
          <p className="payment-page-subtitle">AI-powered mock interviews with instant feedback — pay only when you're ready.</p>
        </div>

        {/* Trial offer urgency banner */}
        {isTrialOffer && (
          <div className="payment-page-trial-offer-banner">
            <span className="payment-page-trial-offer-badge">⏳ 50% OFF</span>
            <span className="payment-page-trial-offer-text">
              Trial offer — <strong>valid for 2 days only</strong>. Prices shown below are already discounted.
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="payment-page-error">
            <span>{error}</span>
            <button 
              className="payment-page-error-close"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Free Trial banner — shown before plans so hesitant visitors see it first */}
        <div className="payment-page-trial-banner">
          <span className="payment-page-trial-badge">FREE</span>
          <div className="payment-page-trial-text">
            <p className="payment-page-trial-heading">Try the AI interviewer — completely free</p>
            <p className="payment-page-trial-sub">2 full questions, instant feedback. No card, no commitment.</p>
          </div>
          <button
            className="payment-page-trial-btn"
            onClick={() => navigate('/setup')}
            disabled={loading}
          >
            Start Free — No Card Needed →
          </button>
        </div>

        {/* Plans grid - horizontal layout */}
        <div className="payment-page-plans-grid">
          {plans.map((plan) => {
            const priceInfo = getPriceForPlan(plan.id);
            
            return (
              <div key={plan.id} className={`payment-page-plan-card${plan.recommended ? ' payment-page-plan-card--recommended' : ''}`}>
                <h3 className="payment-page-plan-name">{plan.name}</h3>
                <p className="payment-page-plan-description">{plan.description}</p>
                
                {/* Price Display */}
                {priceInfo.hasDiscount ? (
                  <div className="payment-page-plan-price-container">
                    <div className="payment-page-plan-price-original">₹{priceInfo.original}</div>
                    <div className="payment-page-plan-price-final" style={{
                      color: priceInfo.final === 0 ? '#10b981' : '#10b981',
                      fontSize: priceInfo.final === 0 ? '20px' : '22px'
                    }}>
                      {priceInfo.final === 0 ? 'FREE' : `₹${priceInfo.final}`}
                    </div>
                    <div className="payment-page-discount-badge">
                      {priceInfo.final === 0 ? 'FREE!' : `${priceInfo.discountPercentage.toFixed(0)}% OFF`}
                    </div>
                  </div>
                ) : (
                  <div className="payment-page-plan-price">{plan.priceDisplay}</div>
                )}
                
                <p className="payment-page-plan-per-unit">{plan.perUnit}</p>

                {/* Buy Now button */}
                <button
                  className="payment-page-buy-button"
                  onClick={() => handleBuy(plan.id)}
                  disabled={loading || processingPlan !== null}
                  style={{
                    background: priceInfo.final === 0 ? '#10b981' : '#667eea',
                    borderColor: priceInfo.final === 0 ? '#10b981' : '#667eea'
                  }}
                >
                  {processingPlan === plan.id ? 'Processing...' : (priceInfo.final === 0 ? '🎉 Get Free Access' : 'Buy Now')}
                </button>
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div className="payment-page-divider">
          <span>OR use access code</span>
        </div>

        {/* Three code input sections */}
        <div className="payment-page-codes-section">
          
          {/* Coupon Code */}
          <div className="payment-page-code-group">
            <label htmlFor="coupon-code" className="payment-page-code-label">
              🎟️ Coupon / Discount Code
            </label>
            <div className="payment-page-code-input-row">
              <input
                id="coupon-code"
                type="text"
                className="payment-page-code-input"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setError(null); }}
                disabled={loading || validatingCoupon}
                maxLength={20}
              />
              {appliedCoupon && appliedCoupon.valid && (
                <span className="payment-page-code-applied">
                  ✓ Coupon Applied
                </span>
              )}
            </div>
            {couponCode && !appliedCoupon && (
              <button
                className="payment-page-apply-code-btn"
                onClick={handleApplyCoupon}
                disabled={loading || validatingCoupon || processingPlan !== null}
                style={{ marginTop: '10px' }}
              >
                {validatingCoupon ? 'Validating...' : 'Apply Coupon'}
              </button>
            )}
            {appliedCoupon && (
              <button className="payment-page-remove-code" onClick={handleRemoveCoupon}>
                Remove Coupon
              </button>
            )}
          </div>

          {/* College Code — backend not yet ready */}
          <div className="payment-page-code-group">
            <label htmlFor="college-code" className="payment-page-code-label">
              🎓 College Code
            </label>
            <div className="payment-page-code-input-row">
              <input
                id="college-code"
                type="text"
                className="payment-page-code-input"
                placeholder="Coming soon"
                disabled={true}
              />
            </div>
            <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>
              College access codes are coming soon.
            </p>
          </div>

          {/* Company Code */}
          <div className="payment-page-code-group">
            <label htmlFor="company-code" className="payment-page-code-label">
              🏢 Company / Job Code
            </label>
            <div className="payment-page-code-input-row">
              <input
                id="company-code"
                type="text"
                className="payment-page-code-input"
                placeholder="Enter company or job code"
                value={companyCode}
                onChange={(e) => { setCompanyCode(e.target.value); setError(null); }}
                disabled={loading || validatingCompany}
                maxLength={30}
              />
              {appliedCompany && (
                <span className="payment-page-code-applied">
                  ✓ Verified
                </span>
              )}
            </div>
            {companyCode && !appliedCompany && (
              <button
                className="payment-page-apply-code-btn"
                onClick={handleApplyCompany}
                disabled={loading || validatingCompany || processingPlan !== null}
                style={{ marginTop: '10px' }}
              >
                {validatingCompany ? 'Validating...' : 'Apply Company Code'}
              </button>
            )}
            {appliedCompany && companyJobInfo && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.6rem 0.75rem',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem',
                fontSize: '0.85rem',
                color: '#d1fae5'
              }}>
                {companyJobInfo.company_name && (
                  <div>🏢 <strong>{companyJobInfo.company_name}</strong></div>
                )}
                {companyJobInfo.job_name && (
                  <div style={{ marginTop: '0.2rem' }}>💼 Role: {companyJobInfo.job_name}</div>
                )}
              </div>
            )}
            {appliedCompany && (
              <button className="payment-page-remove-code" onClick={handleRemoveCompany}>
                Remove Company Code
              </button>
            )}
          </div>

        </div>

        {/* Trust footer */}
        <div className="payment-page-trust-footer">
          <span className="payment-page-trust-icon">🔒</span>
          <span className="payment-page-trust-text">
            100% Secure · Powered by <strong>Razorpay</strong> · UPI / Cards / Netbanking / Wallets
          </span>
        </div>
      </div>
    </div>
  );
}
